// Urvalet: vilka tjänster ur katalogen som kunden behöver och de mängder som
// priset beror på. AI tar fram urvalet, och prismotorn räknar priset med
// calculateQuote(catalog, selection). Se "Prismotor och tjänstekatalog" och
// "AI-tolkning" i docs/ARCHITECTURE.md.
import { z } from 'zod';
import {
  driverSchema,
  LEGAL_FORM_NAMES,
  legalFormSchema,
  serviceIdSchema,
  type Pricing,
  type ServiceCatalog,
} from './catalog.js';

/** En vald tjänst med eventuell mängd och AI:s motivering. */
export const selectedServiceSchema = z.object({
  serviceId: serviceIdSchema,
  /** Mängd för tjänster som prissätts per enhet, till exempel antal anställda. */
  quantity: z.int().positive().optional(),
  /** Varför tjänsten behövs. Byrån ser motiveringen, men kunden ser den inte. */
  reason: z.string().trim().min(1).max(500),
});
export type SelectedService = z.infer<typeof selectedServiceSchema>;

/** Om kunden har angett siffran eller om AI har uppskattat den. */
export const driverSourceSchema = z.enum(['stated', 'estimated']);
export type DriverSource = z.infer<typeof driverSourceSchema>;

/**
 * Värdet för en drivare, till exempel transactionsPerMonth. En uppskattning
 * har ett intervall (low–high). En angiven siffra har low = value = high.
 */
export const driverValueSchema = z
  .object({
    value: z.number().nonnegative(),
    low: z.number().nonnegative(),
    high: z.number().nonnegative(),
    source: driverSourceSchema,
  })
  .refine((d) => d.low <= d.value && d.value <= d.high, {
    message: 'Värdet måste ligga inom intervallet, low ≤ value ≤ high',
  })
  .refine((d) => d.source === 'estimated' || (d.low === d.value && d.high === d.value), {
    message: 'En angiven siffra har inget intervall, så low och high ska vara lika med value',
  });
export type DriverValue = z.infer<typeof driverValueSchema>;

/** En justering av priset i procent, positiv eller negativ. */
export const adjustmentSchema = z.object({
  percent: z.number().min(-100).max(100),
  /** Varför priset justeras. Byrån ser motiveringen, men kunden ser den inte. */
  reason: z.string().trim().min(1).max(500),
});
export type Adjustment = z.infer<typeof adjustmentSchema>;

export const selectionSchema = z.object({
  /** Kundens bolagsform, som kunden väljer i formuläret. */
  legalForm: legalFormSchema,
  /** Tjänster som räknas in i offertens summor. */
  services: z.array(selectedServiceSchema),
  /** Tjänster som kan bli aktuella. De får pris men räknas inte in i summan. */
  optionalServices: z.array(selectedServiceSchema),
  /** Mängder som priset beror på, med drivarens namn som nyckel. */
  drivers: z.record(driverSchema, driverValueSchema),
  /**
   * AI:s förslag på justering av priset i procent, med motivering. Prismotorn
   * begränsar den till byråns intervall i katalogen.
   */
  adjustment: adjustmentSchema.optional(),
});
export type Selection = z.infer<typeof selectionSchema>;

/**
 * Ett schema för urval mot en viss katalog. Det godtar bara tjänster som finns
 * i katalogen och kräver de mängder och drivare som tjänsterna behöver, så att
 * AI inte kan hitta på en tjänst och prismotorn alltid har det den behöver.
 */
export function createSelectionSchema(catalog: ServiceCatalog) {
  return selectionSchema.superRefine((selection, ctx) => {
    for (const issue of catalogIssues(catalog, selection)) {
      ctx.addIssue({ code: 'custom', ...issue });
    }
  });
}

/** Ett fel i ett urval jämfört med katalogen, med sökvägen till fältet. */
export type CatalogIssue = { path: (string | number)[]; message: string };

/**
 * Kontrollerar valda tjänster och drivare mot katalogen: att tjänsterna finns
 * och bara väljs en gång, att tjänster per enhet har en mängd och att alla
 * drivare som tjänsterna behöver har ett värde. Används av urvalet här och av
 * AI:s svar i packages/ai, så att båda kontrolleras på samma sätt.
 */
export function catalogIssues(
  catalog: ServiceCatalog,
  selection: Pick<Selection, 'services' | 'optionalServices' | 'drivers'>,
): CatalogIssue[] {
  const servicesById = new Map(catalog.services.map((service) => [service.id, service]));
  const issues: CatalogIssue[] = [];
  const seen = new Set<string>();
  const missingDrivers = new Set<string>();

  for (const list of ['services', 'optionalServices'] as const) {
    selection[list].forEach((selected, i) => {
      const path = [list, i];
      const service = servicesById.get(selected.serviceId);

      if (!service) {
        issues.push({
          path: [...path, 'serviceId'],
          message: `Tjänsten ${selected.serviceId} finns inte i katalogen`,
        });
        return;
      }

      if (seen.has(selected.serviceId)) {
        issues.push({
          path: [...path, 'serviceId'],
          message: `Tjänsten ${selected.serviceId} är vald mer än en gång`,
        });
      }
      seen.add(selected.serviceId);

      if (service.pricing.type === 'perUnit' && selected.quantity === undefined) {
        issues.push({
          path: [...path, 'quantity'],
          message: `Tjänsten ${service.id} prissätts per ${service.pricing.unit} och behöver en mängd`,
        });
      }

      for (const driver of driversFor(service.pricing)) {
        if (!(driver in selection.drivers) && !missingDrivers.has(driver)) {
          missingDrivers.add(driver);
          issues.push({
            path: ['drivers', driver],
            message: `Tjänsten ${service.id} behöver ett värde för ${driver}`,
          });
        }
      }
    });
  }
  return issues;
}

/** Drivarna som ett prissättningssätt behöver. */
function driversFor(pricing: Pricing): string[] {
  switch (pricing.type) {
    case 'tiered':
      return [pricing.driver];
    case 'hourly':
      return pricing.timeNorms.flatMap((norm) => ('driver' in norm ? [norm.driver] : []));
    default:
      return [];
  }
}

/**
 * Lägger till de tjänster som katalogen säger alltid ska med för kundens
 * bolagsform, till exempel bokslutet (ADR 0101). AI föreslår, koden
 * bestämmer: har AI missat tjänsten, eller bara tagit den som tillval, tas
 * den med som tjänst. Används när en offert räknas från AI:s tolkning, men
 * inte när byrån själv har ändrat raderna.
 */
export function addRequiredServices(catalog: ServiceCatalog, selection: Selection): Selection {
  const valda = new Set(selection.services.map((s) => s.serviceId));
  const obligatoriska = catalog.services.filter(
    (s) => s.requiredFor?.includes(selection.legalForm) && !valda.has(s.id),
  );
  if (obligatoriska.length === 0) return selection;
  const tillagda = new Set(obligatoriska.map((s) => s.id));
  const reason = `Ingår alltid för ${LEGAL_FORM_NAMES[selection.legalForm].toLowerCase()}`;
  return {
    ...selection,
    services: [...selection.services, ...obligatoriska.map((s) => ({ serviceId: s.id, reason }))],
    optionalServices: selection.optionalServices.filter((s) => !tillagda.has(s.serviceId)),
  };
}
